import { useEffect, useState } from "react";
import SbuxEnhancedTable from "../../../../components/SbuxTable/SbuxEnhancedTable";
import MetricsRow from "./MetricsRow";
import MyMetricsRow from "./MyMetricsRow";
import SbuxSubTabs from "../../../../components/SbuxTabs/SbuxSubTabs";
import { Box } from "@mui/material";
import styles from "./styles";
import useCss from "../../../../hooks/useCss";
import {
  metricsColumns,
  metricsGroupColumns,
  myMetricsColumns,
} from "./columns";
import {
  getMetricsQueue,
  getMyMetricsData,
} from "../../../../services/metrics";
import { useAppDispatch, useAppSelector } from "../../../../store/types";
import { getCcpQueuesSelector } from "../../../../selectors/ccpSelector";
import {
  getInstanceIdSelector,
  getArnSelector,
} from "../../../../selectors/userInstanceSelector";
import {
  getQueueMetricsSelector,
  getMyMetricsDataSelector,
} from "../../../../selectors/metricsSelector";

interface MetricsTableProps {
  subTabs: unknown[];
}

const MetricsTable = ({ subTabs }: MetricsTableProps) => {
  const classes = useCss(styles);
  const dispatch = useAppDispatch();

  const queues = useAppSelector(getCcpQueuesSelector);
  const instanceId = useAppSelector(getInstanceIdSelector);
  const arn = useAppSelector(getArnSelector);
  const queueMetrics = useAppSelector(getQueueMetricsSelector);
  const metrics = useAppSelector(getMyMetricsDataSelector);

  const [metricId, setMetricId] = useState("queueMetrics");
  const [metricsData, setMetricsData] = useState<Record<string, unknown>[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (metrics) {
      setMetricsData(metrics as Record<string, unknown>[]);
      if (metrics.length > 0) {
        handleSubTabChange(metricId);
      }
    }
  }, [metrics]);

  useEffect(() => {
    loadData();
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      loadData();
    }, 5000);
    return () => clearInterval(interval);
  }, [queues]);

  useEffect(() => {
    if (metricId === "myMetrics" && instanceId) {
      dispatch(getMyMetricsData(arn ? { instanceArn: arn } : {}));
    }
  }, [metricId, instanceId, arn, dispatch]);

  const loadData = async () => {
    const metricsPayload = {
      instanceId,
      queues,
    };
    await dispatch(getMetricsQueue(metricsPayload));
  };

  const loadMyMetricsData = async () => {
    const myMetricsPayload = {
      instanceArn: arn,
    };
    await dispatch(getMyMetricsData(myMetricsPayload));
  };

  const handleRefrehClick = async () => {
    if (metricId === "queueMetrics") await loadData();
    else await loadMyMetricsData();
  };

  const handleSubTabChange = (tab: { value?: string } | string) => {
    if (tab && typeof tab !== "string" && tab.value) {
      setMetricId(tab.value);
    }
  };

  const SubTabsComponent = () => {
    return (
      <SbuxSubTabs
        tabList={
          subTabs as { value: string; name: string; [key: string]: unknown }[]
        }
        tabId={metricId}
        handleTabClick={
          handleSubTabChange as unknown as (tab: {
            value: string;
            name: string;
          }) => void
        }
      />
    );
  };

  return (
    <Box className={classes.container}>
      <SbuxEnhancedTable
        rows={
          (metricId === "queueMetrics" ? queueMetrics : metricsData) as Record<
            string,
            unknown
          >[]
        }
        columns={
          metricId === "queueMetrics" ? metricsColumns : myMetricsColumns
        }
        groupColumns={metricId === "queueMetrics" ? metricsGroupColumns : null}
        defaultSortingField={"contactsInQueue"}
        defaultSortOrder={"desc"}
        RowSource={metricId === "queueMetrics" ? MetricsRow : MyMetricsRow}
        showSearchBar={true}
        showSubTabs={true}
        SubTabs={SubTabsComponent}
        showRefreshButton={metricId === "queueMetrics" ? false : true}
        handleRefreshClick={handleRefrehClick}
        showFooter={true}
        footerMarginBottom={"0"}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default MetricsTable;
